import { useEffect, useRef, useCallback } from "react";
import debounce from "common/debounce";

interface UsePaginatedInfiniteScrollParams {
  loadMore: () => void;
  hasMore: boolean;
  loading: boolean;
  threshold?: number;
}

interface UsePaginatedInfiniteScrollReturn {
  containerRef: React.RefObject<HTMLDivElement>;
  loadMoreRef: React.RefObject<HTMLDivElement>;
}

export default function usePaginatedInfiniteScroll({
  loadMore,
  hasMore,
  loading,
  threshold = 300
}: UsePaginatedInfiniteScrollParams): UsePaginatedInfiniteScrollReturn {
  const containerRef = useRef<HTMLDivElement>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const observer = useRef<IntersectionObserver | null>(null);

  const debouncedLoadMore = useCallback(
    debounce(() => {
      if (hasMore && !loading) {
        loadMore();
      }
    }, 300),
    [loadMore, hasMore, loading]
  );

  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const target = entries[0];
      if (target.isIntersecting && hasMore && !loading) {
        debouncedLoadMore();
      }
    },
    [debouncedLoadMore, hasMore, loading]
  );

  useEffect(() => {
    const currentLoadMoreRef = loadMoreRef.current;

    if (!currentLoadMoreRef) return;

    observer.current = new IntersectionObserver(handleObserver, {
      root: null,
      rootMargin: `0px ${threshold}px`,
      threshold: 0.1
    });

    observer.current.observe(currentLoadMoreRef);

    return () => {
      if (observer.current && currentLoadMoreRef) {
        observer.current.unobserve(currentLoadMoreRef);
        observer.current.disconnect();
      }
    };
  }, [handleObserver, threshold]);

  useEffect(() => {
    return () => {
      debouncedLoadMore.cancel();
    };
  }, [debouncedLoadMore]);

  return { containerRef, loadMoreRef };
}
